import { cn, NoddiIcon } from 'noddi-ui-common';
import { useFullScreenBodyLock } from 'noddi-ui-web';
import { useState } from 'react';
import { PhotoLightbox, type LightboxPhoto } from './PhotoLightbox';

type Props = {
  photos: LightboxPhoto[];
  /** Overrides the default two-up grid. */
  className?: string;
};

/** 4:3 photo tiles that open a lightbox over just these photos. */
export const PhotoGrid = ({ photos, className }: Props) => {
  const { isFullScreen, setIsFullScreen, ref } = useFullScreenBodyLock();
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div ref={ref} className={cn('grid grid-cols-2 gap-2', className)}>
      {photos.map((photo, index) => (
        <PhotoTile
          key={`${photo.imageUrl}-${index}`}
          imageUrl={photo.imageUrl}
          label={photo.label}
          onClick={() => {
            setActiveIndex(index);
            setIsFullScreen(true);
          }}
        />
      ))}

      {isFullScreen && (
        <PhotoLightbox
          photos={photos}
          index={activeIndex}
          onIndexChange={setActiveIndex}
          onClose={() => setIsFullScreen(false)}
        />
      )}
    </div>
  );
};

type PhotoTileProps = {
  imageUrl: string;
  label: string;
  onClick: () => void;
};

export const PhotoTile = ({ imageUrl, label, onClick }: PhotoTileProps) => {
  const [failed, setFailed] = useState(false);

  return (
    <button
      type='button'
      onClick={onClick}
      className='group relative aspect-[4/3] cursor-pointer overflow-hidden rounded-lg bg-lightPurple ring-secondary transition-shadow hover:ring-2'
    >
      {failed ? (
        <span className='flex size-full flex-col items-center justify-center gap-2 px-4 text-muted-foreground'>
          <NoddiIcon name='CameraOff' />
          <span className='text-xs'>{label}</span>
        </span>
      ) : (
        <img
          src={imageUrl}
          alt={label}
          referrerPolicy='no-referrer'
          className='size-full object-cover'
          onError={() => setFailed(true)}
        />
      )}
      {!failed && (
        <span className='absolute top-1.5 left-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-black backdrop-blur-xs'>
          {label}
        </span>
      )}
      <span className='absolute right-1.5 bottom-1.5 flex size-6 items-center justify-center rounded-[7px] bg-white/90 text-darkPurple'>
        <NoddiIcon name='Maximize2' size={14} />
      </span>
    </button>
  );
};
